import React, { useState, useEffect } from 'react';
import Layout from '../../Global/Layout';
import DataTable from '../../Global/DataTable';
import { ROLE_DISPLAY, getCurrentRole, API_CONFIG } from '../../../contexts/constants';
import { formatDateTimeTable } from '../../../utils/dateFormatter';
import { LottoBallsDisplay } from '../../../utils/LottoBalls';
import { formatTHB } from '../../../utils/numberFormat';
import axios from 'axios';

const DashboardPage3 = ({ onLogout, userRole }) => {
	const [activeTab, setActiveTab] = useState('tab1');

	// Statistics states
	const [stats, setStats] = useState({
		totalCustomers: 0,
		totalAgents: 0,
		totalOrders: 0,
		totalAmount: 0
	});
	const [loadingStats, setLoadingStats] = useState(true);

	// Tab 1 states (Recent Orders)
	const [orders, setOrders] = useState([]);
	const [loading1, setLoading1] = useState(false);
	const [currentPage1, setCurrentPage1] = useState(1);
	const [pageSize1, setPageSize1] = useState(10);
	const [totalItems1, setTotalItems1] = useState(0);

	// Tab 2 states (Top Agents)
	const [agents, setAgents] = useState([]);
	const [loading2, setLoading2] = useState(false);
	const [currentPage2, setCurrentPage2] = useState(1);
	const [pageSize2, setPageSize2] = useState(10);
	const [totalItems2, setTotalItems2] = useState(0);

	// Tab 3 states (Other Stats)
	const [otherStats, setOtherStats] = useState([]);
	const [loading3, setLoading3] = useState(false);

	const currentUserRole = getCurrentRole(userRole);

	useEffect(() => {
		// Check permission first
		if (!['sysadmin', 'owner', 'admin'].includes(userRole)) {
			return;
		}

		const fetchOrders = async () => {
			try {
				setLoading1(true);
				const token = localStorage.getItem('token');
				const response = await axios.get(`${API_CONFIG.BASE_URL}/dashboard/owner/recent-orders`, {
					headers: { Authorization: `Bearer ${token}` },
					params: { page: currentPage1, pageSize: pageSize1 }
				});
				if (response.data.success) {
					setOrders(response.data.data.orders);
					setTotalItems1(response.data.data.totalItems);
				}
			} catch (error) {
				console.error('Error fetching orders:', error);
			} finally {
				setLoading1(false);
			}
		};
		const fetchStats = async () => {
			try {
				setLoadingStats(true);
				const token = localStorage.getItem('token');
				const response = await axios.get(`${API_CONFIG.BASE_URL}/dashboard/owner/stats`, {
					headers: { Authorization: `Bearer ${token}` }
				});
				if (response.data.success) {
					setStats(response.data.data);
				}
			} catch (error) {
				console.error('Error fetching stats:', error);
			} finally {
				setLoadingStats(false);
			}
		};
		const fetchAgents = async () => {
			try {
				setLoading2(true);
				const token = localStorage.getItem('token');
				const response = await axios.get(`${API_CONFIG.BASE_URL}/dashboard/owner/top-agents`, {
					headers: { Authorization: `Bearer ${token}` },
					params: { page: currentPage2, pageSize: pageSize2 }
				});
				if (response.data.success) {
					setAgents(response.data.data.agents);
					setTotalItems2(response.data.data.totalItems);
				}
			} catch (error) {
				console.error('Error fetching agents:', error);
			} finally {
				setLoading2(false);
			}
		};
		const fetchOtherStats = async () => {
			try {
				setLoading3(true);
				const token = localStorage.getItem('token');
				const response = await axios.get(`${API_CONFIG.BASE_URL}/dashboard/owner/other-stats`, {
					headers: { Authorization: `Bearer ${token}` }
				});
				if (response.data.success) {
					setOtherStats(response.data.data.stats);
				}
			} catch (error) {
				console.error('Error fetching other stats:', error);
			} finally {
				setLoading3(false);
			}
		};

		fetchStats();
		fetchOrders();
		fetchAgents();
		fetchOtherStats();
	}, [activeTab, currentPage1, pageSize1, currentPage2, pageSize2]);

	if (!['sysadmin', 'owner', 'admin'].includes(userRole)) {
		window.location.href = '/forbidden';
		return null;
	}

	const columns1 = [
		{
			key: 'create_date',
			className: 'text-center',
			width: '170px',
			header: 'เวลาที่สั่งซื้อ',
			render: (row) => (
				<div className="flex gap-2 justify-center">
					{formatDateTimeTable(row.create_date)}
				</div>
			)
		},
		{ key: 'user_name', header: `ชื่อ${ROLE_DISPLAY['customer']}` },
		{
			key: 'type',
			header: 'ประเภท',
			render: (row) => (
			<div className="text-center">
				<span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold min-w-32 ${
				row.type === 'powerball'
					? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
					: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
				}`}>
				{row.type}
				</span>
			</div>
			)
		},
		{
			key: 'results',
			header: 'ผลรางวัล',
			className: 'text-center',
			render: (row) => {
				return <LottoBallsDisplay grayBalls={row.grayBalls} specialBall={row.redBall} type={row.type} />;
			}
		},
		{ key: 'admin_name', header: `ชื่อ${ROLE_DISPLAY['agent']}` },
	];
	const columns2 = [
		{
			key: 'no',
			header: 'ลำดับ',
			className: 'text-center',
			width: 100,
			render: (row) => (
				<div className="flex gap-2 justify-center">{row.no}</div>
			)
		},
		{ key: 'admin_name', header: `ชื่อ${ROLE_DISPLAY['agent']}` },
		{
			key: 'user_all_count',
			header: `จำนวน${ROLE_DISPLAY['customer']}ทั้งหมด`,
			className: 'text-center',
			render: (row) => (
				<div className="flex justify-center">
					<span className="font-semibold">
						{row.user_all_count}
					</span>
				</div>
			)
		},
		{
			key: 'user_count',
			header: `จำนวน${ROLE_DISPLAY['customer']}ที่สั่งซื้อ`,
			className: 'text-center',
			render: (row) => (
				<div className="flex justify-center">
					<span className="font-semibold">
						{row.user_count}
					</span>
				</div>
			)
		},
		{
			key: 'create_date',
			header: 'เวลาที่สั่งซื้อล่าสุด',
			className: 'text-center',
			width: '170px',
			render: (row) => (
				<div className="flex justify-center">
					{formatDateTimeTable(row.create_date)}
				</div>
			)
		},
	];

	const totalPages1 = Math.max(1, Math.ceil(totalItems1 / pageSize1));
	const safeCurrentPage1 = Math.min(currentPage1, totalPages1);

	const totalPages2 = Math.max(1, Math.ceil(totalItems2 / pageSize2));
	const safeCurrentPage2 = Math.min(currentPage2, totalPages2);

  	const gotoPage1 = (p) => {
		if (p < 1 || p > totalPages1) return;
		setCurrentPage1(p);
	};

	const gotoPage2 = (p) => {
		if (p < 1 || p > totalPages2) return;
		setCurrentPage2(p);
	};

	return (
		<Layout title={`หน้าแรก ${ROLE_DISPLAY[currentUserRole]}`} onLogout={onLogout} userRole={currentUserRole}>
		<div className="space-y-6">
			{/* Statistics Cards */}
			<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
			<div className="bg-blue-500 dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
				<p className="text-sm text-white">{ROLE_DISPLAY['customer']}ทั้งหมด</p>
				<p className="text-2xl font-bold text-white mt-1">
					{loadingStats ? '...' : formatTHB(stats.totalCustomers)} <span className='text-sm'>คน</span>
				</p>
			</div>
			<div className="bg-red-500 dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
				<p className="text-sm text-white">{ROLE_DISPLAY['agent']}ทั้งหมด</p>
				<p className="text-2xl font-bold text-white mt-1">
					{loadingStats ? '...' : formatTHB(stats.totalAgents)} <span className='text-sm'>คน</span>
				</p>
			</div>
			<div className="bg-green-500 dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
				<p className="text-sm text-white">จำนวนการสั่งซื้องวดปัจจุบัน</p>
				<p className="text-2xl font-bold text-white mt-1">
					{loadingStats ? '...' : formatTHB(stats.totalOrders)} <span className='text-sm'>ใบ</span>
				</p>
			</div>
			<div className="bg-yellow-500 dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
				<p className="text-sm text-white">จำนวนเงินที่ถูกสั่งซื้องวดปัจจุบัน</p>
				<p className="text-2xl font-bold text-white mt-1">
					{loadingStats ? '...' : formatTHB(stats.totalAmount)} <span className='text-sm'>บาท</span>
				</p>
			</div>
			</div>

					{/* Tabs Navigation */}
			<div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
			<div className="flex border-b border-gray-200 dark:border-gray-700 grid grid-cols-1 md:grid-cols-3">
				<button
					onClick={() => setActiveTab('tab1')}
					className={`flex-1 px-6 py-4 font-medium transition ${
						activeTab === 'tab1'
						? 'bg-blue-50 dark:bg-[#2a2a2a] text-black dark:text-white/90'
						: 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
					}`}
					>
					รายการสั่งซื้อล่าสุด
				</button>
				<button
					onClick={() => setActiveTab('tab2')}
					className={`flex-1 px-6 py-4 font-medium transition ${
						activeTab === 'tab2'
						? 'bg-blue-50 dark:bg-[#2a2a2a] text-black dark:text-white/90'
						: 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
					}`}
					>
					{ROLE_DISPLAY['agent']}ที่มี{ROLE_DISPLAY['customer']}สั่งซื้อที่สุด
				</button>
				<button
					onClick={() => setActiveTab('tab3')}
					className={`flex-1 px-6 py-4 font-medium transition ${
						activeTab === 'tab3'
						? 'bg-blue-50 dark:bg-[#2a2a2a] text-black dark:text-white/90'
						: 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
					}`}
					>
					รายละเอียดอื่นๆ
				</button>
			</div>

			{/* Tab Content */}
			<div className="p-6">
				{activeTab === 'tab1' && (
				<div className="space-y-4">
					<h3 className="text-xl font-bold text-gray-900 dark:text-white">รายการที่{ROLE_DISPLAY['customer']}สั่งซื้อ</h3>
					<p className="text-gray-600 dark:text-gray-400">
					รายการที่{ROLE_DISPLAY['customer']}ได้ทำการสั่งซื้อล่าสุด ในงวดปัจจุบัน
					</p>
					<DataTable
						columns={columns1}
						data={orders}
						loading={loading1}
						page={safeCurrentPage1}
						pageSize={pageSize1}
						totalItems={totalItems1}
						onPageChange={gotoPage1}
						onPageSizeChange={(size) => {
							setPageSize1(size);
							setCurrentPage1(1);
						}}
						emptyMessage="ไม่มีข้อมูลสินค้า"
					/>
				</div>
				)}

				{activeTab === 'tab2' && (
				<div className="space-y-4">
					<h3 className="text-xl font-bold text-gray-900 dark:text-white">จำนวนรายการสั่งซื้อของ{ROLE_DISPLAY['customer']}</h3>
					<p className="text-gray-600 dark:text-gray-400">
						รายชื่อ{ROLE_DISPLAY['agent']}ที่มี{ROLE_DISPLAY['customer']}สั่งซื้อมากที่สุด ในงวดปัจจุบัน
					</p>
					<DataTable
						columns={columns2}
						data={agents}
						loading={loading2}
						page={safeCurrentPage2}
						pageSize={pageSize2}
						totalItems={totalItems2}
						onPageChange={gotoPage2}
						onPageSizeChange={(size) => {
							setPageSize2(size);
							setCurrentPage2(1);
						}}
						emptyMessage="ไม่มีข้อมูลสินค้า"
					/>
				</div>
				)}

				{activeTab === 'tab3' && (
				<div className="space-y-4">
					<h3 className="text-xl font-bold text-gray-900 dark:text-white">อื่นๆ</h3>
					<p className="text-gray-600 dark:text-gray-400">
						รายละเอียดอื่นๆ ที่น่าสนใจเกี่ยวกับ{ROLE_DISPLAY['customer']}และ{ROLE_DISPLAY['agent']}
					</p>
					{loading3 ? (
						<div className="text-center py-8">กำลังโหลด...</div>
					) : otherStats.length === 0 ? (
						<div className="text-center py-8 text-gray-500">ไม่มีข้อมูล</div>
					) : (
					<div className="space-y-3">
					{otherStats.map(item => (
						<div key={item.no} className="flex items-center gap-4 bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
						<div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold">
							{item.no}
						</div>
						<div className="flex-1">
							<h4 className="font-semibold text-gray-900 dark:text-white">{item.title}</h4>
							<p className="text-sm text-gray-600 dark:text-gray-400">{item.detail}</p>
						</div>
						</div>
					))}
					</div>
					)}
				</div>
				)}
			</div>
			</div>
		</div>
		</Layout>
	);
};

export default DashboardPage3;
